/**
 * Accountability service layer.
 * Abstracts all Firestore operations for the accountability partner system,
 * daily check-ins, and partnership management.
 */
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const PAIRS_COLLECTION = 'accountabilityPairs';
const CHECKINS_SUBCOLLECTION = 'checkIns';
const USERS_COLLECTION = 'users';

/**
 * Serialize a Firestore accountability pair document snapshot into a plain object.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {Object}
 */
const serializePair = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    users: data.users || [],
    userNames: data.userNames || [],
    userUsernames: data.userUsernames || [],
    userAvatars: data.userAvatars || [],
    status: data.status || 'pending',
    createdAt: data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : data.createdAt || new Date().toISOString(),
    endedAt: data.endedAt instanceof Timestamp
      ? data.endedAt.toDate().toISOString()
      : data.endedAt || null,
  };
};

/**
 * Serialize a check-in document snapshot.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {Object}
 */
const serializeCheckIn = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    userId: data.userId || '',
    date: data.date instanceof Timestamp
      ? data.date.toDate().toISOString()
      : data.date || new Date().toISOString(),
    mood: data.mood || 'okay',
    workedOut: data.workedOut || false,
    ateMeal: data.ateMeal || false,
    message: data.message || '',
    createdAt: data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : data.createdAt || new Date().toISOString(),
    _docSnapshot: docSnap,
  };
};

/**
 * Find a potential accountability partner for the user.
 * Simple matching: random active user without an active partnership.
 * @param {string} userId - The current user's ID.
 * @returns {Promise<Object|null>} A user profile object or null if no match found.
 */
export const findPartner = async (userId) => {
  try {
    // Get users who don't currently have an accountability partner
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(
      usersRef,
      orderBy('createdAt', 'desc'),
      firestoreLimit(50)
    );

    const querySnapshot = await getDocs(q);
    const candidates = [];

    for (const docSnap of querySnapshot.docs) {
      if (docSnap.id === userId) continue;
      const data = docSnap.data();
      // Skip users who already have an active partner
      if (data.accountabilityPartnerId) continue;
      candidates.push({ id: docSnap.id, ...data });
    }

    if (candidates.length === 0) return null;

    // Return a random candidate
    const randomIndex = Math.floor(Math.random() * candidates.length);
    return candidates[randomIndex];
  } catch (error) {
    handleServiceError(error, 'Find partner', 'Failed to find a partner. Please try again.');
  }
};

/**
 * Send an accountability partner request.
 * @param {string} fromUserId - The sender's user ID.
 * @param {string} toUserId - The recipient's user ID.
 * @param {Object} fromUserData - Sender's profile data { displayName, username, avatarUrl }.
 * @param {Object} toUserData - Recipient's profile data { displayName, username, avatarUrl }.
 * @returns {Promise<Object>} The created pair object.
 */
export const sendPartnerRequest = async (fromUserId, toUserId, fromUserData, toUserData) => {
  try {
    const pairRef = doc(collection(db, PAIRS_COLLECTION));
    const pairId = pairRef.id;

    const data = {
      users: [fromUserId, toUserId],
      userNames: [fromUserData.displayName || '', toUserData.displayName || ''],
      userUsernames: [fromUserData.username || '', toUserData.username || ''],
      userAvatars: [fromUserData.avatarUrl || '', toUserData.avatarUrl || ''],
      status: 'pending',
      createdAt: serverTimestamp(),
      endedAt: null,
    };

    await setDoc(pairRef, data);

    return {
      id: pairId,
      ...data,
      createdAt: new Date().toISOString(),
    };
  } catch (error) {
    handleServiceError(error, 'Send partner request', 'Failed to send partner request. Please try again.');
  }
};

/**
 * Accept an accountability partner request.
 * @param {string} pairId - The pair document ID.
 * @returns {Promise<void>}
 */
export const acceptPartnerRequest = async (pairId) => {
  try {
    const pairRef = doc(db, PAIRS_COLLECTION, pairId);
    const pairSnap = await getDoc(pairRef);

    if (!pairSnap.exists()) {
      throw new Error('Partner request not found.');
    }

    const pairData = pairSnap.data();
    await updateDoc(pairRef, { status: 'active' });

    // Update both users' accountabilityPartnerId
    const [userId1, userId2] = pairData.users;
    const user1Ref = doc(db, USERS_COLLECTION, userId1);
    const user2Ref = doc(db, USERS_COLLECTION, userId2);
    await updateDoc(user1Ref, { accountabilityPartnerId: userId2 });
    await updateDoc(user2Ref, { accountabilityPartnerId: userId1 });
  } catch (error) {
    handleServiceError(error, 'Accept partner request', 'Failed to accept partner request. Please try again.');
  }
};

/**
 * Decline an accountability partner request.
 * @param {string} pairId - The pair document ID.
 * @returns {Promise<void>}
 */
export const declinePartnerRequest = async (pairId) => {
  try {
    const pairRef = doc(db, PAIRS_COLLECTION, pairId);
    await deleteDoc(pairRef);
  } catch (error) {
    handleServiceError(error, 'Decline partner request', 'Failed to decline partner request. Please try again.');
  }
};

/**
 * End an accountability partnership.
 * @param {string} pairId - The pair document ID.
 * @returns {Promise<void>}
 */
export const endPartnership = async (pairId) => {
  try {
    const pairRef = doc(db, PAIRS_COLLECTION, pairId);
    const pairSnap = await getDoc(pairRef);

    if (!pairSnap.exists()) {
      throw new Error('Partnership not found.');
    }

    const pairData = pairSnap.data();
    await updateDoc(pairRef, {
      status: 'ended',
      endedAt: serverTimestamp(),
    });

    // Clear both users' accountabilityPartnerId
    const [userId1, userId2] = pairData.users;
    const user1Ref = doc(db, USERS_COLLECTION, userId1);
    const user2Ref = doc(db, USERS_COLLECTION, userId2);
    await updateDoc(user1Ref, { accountabilityPartnerId: null });
    await updateDoc(user2Ref, { accountabilityPartnerId: null });
  } catch (error) {
    handleServiceError(error, 'End partnership', 'Failed to end partnership. Please try again.');
  }
};

/**
 * Get the active accountability partnership for a user.
 * @param {string} userId - The user's ID.
 * @returns {Promise<Object|null>} The active pair object or null.
 */
export const getPartnership = async (userId) => {
  try {
    const pairsRef = collection(db, PAIRS_COLLECTION);
    const q = query(
      pairsRef,
      where('users', 'array-contains', userId),
      where('status', '==', 'active')
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return null;

    return serializePair(querySnapshot.docs[0]);
  } catch (error) {
    handleServiceError(error, 'Get partnership', 'Failed to load partnership. Please try again.');
  }
};

/**
 * Get pending partnership requests for a user (where they are the recipient).
 * @param {string} userId - The user's ID.
 * @returns {Promise<Array>} Array of pending pair objects.
 */
export const getPendingRequests = async (userId) => {
  try {
    const pairsRef = collection(db, PAIRS_COLLECTION);
    const q = query(
      pairsRef,
      where('users', 'array-contains', userId),
      where('status', '==', 'pending')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map(serializePair)
      .filter((pair) => {
        // Only show requests where the user is the recipient (second in array)
        return pair.users[1] === userId;
      });
  } catch (error) {
    handleServiceError(error, 'Get pending requests', 'Failed to load pending requests. Please try again.');
  }
};

/**
 * Submit a daily check-in for an accountability pair.
 * @param {string} pairId - The pair document ID.
 * @param {Object} checkInData - The check-in data.
 * @param {string} checkInData.userId - The user's ID.
 * @param {string} checkInData.mood - Mood: 'great' | 'good' | 'okay' | 'tough' | 'bad'.
 * @param {boolean} checkInData.workedOut - Whether the user worked out.
 * @param {boolean} checkInData.ateMeal - Whether the user tracked nutrition.
 * @param {string} checkInData.message - A brief check-in note.
 * @returns {Promise<Object>} The created check-in object.
 */
export const submitCheckIn = async (pairId, checkInData) => {
  try {
    const checkInsRef = collection(db, PAIRS_COLLECTION, pairId, CHECKINS_SUBCOLLECTION);
    const checkInRef = doc(checkInsRef);
    const checkInId = checkInRef.id;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const data = {
      userId: checkInData.userId,
      date: Timestamp.fromDate(today),
      mood: checkInData.mood || 'okay',
      workedOut: checkInData.workedOut || false,
      ateMeal: checkInData.ateMeal || false,
      message: checkInData.message || '',
      createdAt: serverTimestamp(),
    };

    await setDoc(checkInRef, data);

    return {
      id: checkInId,
      ...data,
      date: today.toISOString(),
      createdAt: new Date().toISOString(),
    };
  } catch (error) {
    handleServiceError(error, 'Submit check-in', 'Failed to submit check-in. Please try again.');
  }
};

/**
 * Get recent check-ins for both partners in a pair.
 * @param {string} pairId - The pair document ID.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - Pagination cursor.
 * @param {number} [pageLimit=14] - Max check-ins per page.
 * @returns {Promise<{checkIns: Array, lastDoc: any, hasMore: boolean}>}
 */
export const getCheckIns = async (pairId, lastDoc = null, pageLimit = 14) => {
  try {
    const checkInsRef = collection(db, PAIRS_COLLECTION, pairId, CHECKINS_SUBCOLLECTION);
    let q;

    const constraints = [orderBy('date', 'desc')];

    if (lastDoc) {
      q = query(checkInsRef, ...constraints, startAfter(lastDoc), firestoreLimit(pageLimit));
    } else {
      q = query(checkInsRef, ...constraints, firestoreLimit(pageLimit));
    }

    const querySnapshot = await getDocs(q);
    const checkIns = querySnapshot.docs.map(serializeCheckIn);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    const hasMore = querySnapshot.docs.length === pageLimit;

    return { checkIns, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get check-ins', 'Failed to load check-ins. Please try again.');
  }
};

/**
 * Check if a user has already checked in today for a given pair.
 * @param {string} pairId - The pair document ID.
 * @param {string} userId - The user's ID.
 * @returns {Promise<Object|null>} The today's check-in or null.
 */
export const getTodayCheckIn = async (pairId, userId) => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const checkInsRef = collection(db, PAIRS_COLLECTION, pairId, CHECKINS_SUBCOLLECTION);
    const q = query(
      checkInsRef,
      where('userId', '==', userId),
      where('date', '>=', Timestamp.fromDate(today)),
      where('date', '<', Timestamp.fromDate(tomorrow))
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return null;

    return serializeCheckIn(querySnapshot.docs[0]);
  } catch (error) {
    handleServiceError(error, 'Get today check-in', 'Failed to check today\'s status. Please try again.');
  }
};
